import React from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";

import Modal from "@components/layout/Modal";
import Label from "@components/form/Label";
import TextInput from "@components/form/TextInput";
import SelectLevel0Resource from "@components/form/SelectLevel0Resource";
import SelectLevel1Resource from "@components/form/SelectLevel1Resource";
import Alert from "@components/shared/Alert";
import { exportSubscribers } from "@services/subscriberSyncUpload";
import { triggerBlobDownload } from "@utils/triggerBlobDownload";
import { toastError } from "@utils/toast";

type SubscriberExportModalProps = {
  open: boolean;
  // Optional pre-selected scheme. When omitted the modal renders an in-modal scheme picker so the
  // export can be launched from the global /polestar/subscribers page without first filtering by
  // scheme. When provided (scheme-tab entry), the scheme is read-only.
  level1Resource?: Record<string, any> | null;
  onClose: () => void;
};

const SubscriberExportModal: React.FunctionComponent<
  SubscriberExportModalProps
> = ({ open, level1Resource, onClose }) => {
  const { t } = useTranslation();

  const isSchemePreselected = Boolean(level1Resource?.id);
  const [pickedLevel1, setPickedLevel1] = React.useState<{ id: string; name: string } | null>(null);
  const activeLevel1 = isSchemePreselected
    ? { id: level1Resource!.id, name: level1Resource!.name }
    : pickedLevel1;

  const [level0ResourceId, setLevel0ResourceId] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [createdFrom, setCreatedFrom] = React.useState("");
  const [createdTo, setCreatedTo] = React.useState("");
  const [updatedFrom, setUpdatedFrom] = React.useState("");
  const [updatedTo, setUpdatedTo] = React.useState("");
  const [exporting, setExporting] = React.useState(false);

  const resetForm = () => {
    setPickedLevel1(null);
    setLevel0ResourceId("");
    setStatus("");
    setCreatedFrom("");
    setCreatedTo("");
    setUpdatedFrom("");
    setUpdatedTo("");
  };

  const handleClose = () => {
    if (exporting) return;
    resetForm();
    onClose();
  };

  const dateRangeError =
    createdFrom && createdTo && createdTo < createdFrom
      ? t("subscriber_sync_upload.export.invalid_date_range")
      : null;

  const updatedDateRangeError =
    updatedFrom && updatedTo && updatedTo < updatedFrom
      ? t("subscriber_sync_upload.export.invalid_updated_date_range")
      : null;

  const statusOptions = [
    { label: t("common.active"), value: "active" },
    { label: t("common.inactive"), value: "inactive" },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (dateRangeError || updatedDateRangeError) return;
    if (!activeLevel1?.id) return;
    setExporting(true);
    try {
      const blob = await exportSubscribers({
        level1ResourceId: activeLevel1.id,
        ...(level0ResourceId && { level0ResourceId }),
        ...(status && { isActive: status === "active" }),
        ...(createdFrom && { createdFrom }),
        ...(createdTo && { createdTo }),
        ...(updatedFrom && { updatedFrom }),
        ...(updatedTo && { updatedTo }),
      });
      triggerBlobDownload(blob, "customers-export.xlsx");
      resetForm();
      onClose();
    } catch (err: any) {
      toastError(
        t("subscriber_sync_upload.message.error", {
          error: err?.response?.data?.message ?? err?.message ?? "unknown",
        }),
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal open={open}>
      <form onSubmit={handleSubmit} className="flex flex-col">
        <div className="border-b p-4">
          <h3
            id="modal-title"
            className="text-lg font-bold leading-6 text-gray-900"
          >
            {t("subscriber_sync_upload.export.modal_title")}
          </h3>
        </div>

        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label htmlFor="export-scheme">{t("common.level1wrs")}</Label>
            {isSchemePreselected ? (
              // Scheme-tab entry: scheme is read-only, mirrors how the wizard's scheme step is
              // skipped when entered from a scheme page.
              <div
                id="export-scheme"
                className="px-3 py-2 border border-gray-200 rounded bg-gray-50 text-gray-700"
              >
                {level1Resource!.name ?? "—"}
              </div>
            ) : (
              // Parent (global) entry: pick the scheme inside the modal so the user does not need
              // to set a scheme filter on the customer list first.
              <SelectLevel1Resource
                inputId="export-scheme"
                value={pickedLevel1?.id ?? ""}
                onChange={selected => {
                  setPickedLevel1(selected ? { id: selected.value, name: selected.label } : null);
                  // Resetting the zone keeps it consistent with the chosen scheme.
                  setLevel0ResourceId("");
                }}
                isClearable
              />
            )}
          </div>

          <div>
            <Label htmlFor="export-level0">{t("common.level0wrs")}</Label>
            <SelectLevel0Resource
              inputId="export-level0"
              level1ResourceId={activeLevel1?.id}
              value={level0ResourceId}
              onChange={selected => setLevel0ResourceId(selected?.value ?? "")}
              isClearable
              isDisabled={!activeLevel1?.id}
            />
          </div>

          <div>
            <Label htmlFor="export-status">{t("common.status")}</Label>
            <Select
              inputId="export-status"
              options={statusOptions}
              value={statusOptions.find(o => o.value === status) ?? null}
              onChange={selected => setStatus(selected?.value ?? "")}
              isClearable
            />
          </div>

          <div>
            <Label htmlFor="export-created-from">
              {t("subscriber_sync_upload.export.from_date")}
            </Label>
            <TextInput
              id="export-created-from"
              type="date"
              value={createdFrom}
              onChange={e => setCreatedFrom(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="export-created-to">
              {t("subscriber_sync_upload.export.to_date")}
            </Label>
            <TextInput
              id="export-created-to"
              type="date"
              value={createdTo}
              onChange={e => setCreatedTo(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="export-updated-from">
              {t("subscriber_sync_upload.export.updated_from_date")}
            </Label>
            <TextInput
              id="export-updated-from"
              type="date"
              value={updatedFrom}
              onChange={e => setUpdatedFrom(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="export-updated-to">
              {t("subscriber_sync_upload.export.updated_to_date")}
            </Label>
            <TextInput
              id="export-updated-to"
              type="date"
              value={updatedTo}
              onChange={e => setUpdatedTo(e.target.value)}
            />
          </div>

          {dateRangeError ? (
            <div className="md:col-span-2">
              <Alert type="error">{dateRangeError}</Alert>
            </div>
          ) : null}
          {updatedDateRangeError ? (
            <div className="md:col-span-2">
              <Alert type="error">{updatedDateRangeError}</Alert>
            </div>
          ) : null}
        </div>

        <footer className="flex gap-2 justify-end border-t p-4">
          <button
            type="button"
            className="btn-outline-primary"
            onClick={handleClose}
            disabled={exporting}
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={
              exporting ||
              !activeLevel1?.id ||
              Boolean(dateRangeError) ||
              Boolean(updatedDateRangeError)
            }
          >
            {t("common.export")}
          </button>
        </footer>
      </form>
    </Modal>
  );
};

export default SubscriberExportModal;
